import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useStateContext } from 'shared/contexts/ContextProvider';
import { storeService } from 'features/company/api';
import { loadList } from 'shared/api/requests';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
    ScatterChart, Scatter, ZAxis, Cell
} from 'recharts';
import * as XLSX from 'xlsx';

const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const formatCurrency = (val) => `${(val / 1000).toFixed(1)}K`;
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6B6B'];

const StoreCategoryComparison = () => {
    const today = formatDate(new Date());
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(formatDate(new Date(new Date().setDate(new Date().getDate() - 7))));
    const [endDate, setEndDate] = useState(today);
    const [selectedStores, setSelectedStores] = useState([]);
    const [hiddenCategories, setHiddenCategories] = useState([]);
    const [activeView, setActiveView] = useState('category-metrics');
    const [topN, setTopN] = useState(10);
    const [sortBy, setSortBy] = useState('sales'); // 'sales' or 'stock'
    const [categoryMetric, setCategoryMetric] = useState('total_sales_sum'); // metric for category comparison
    const { token, showToast } = useStateContext();

    // Metric options for category comparison
    const metricOptions = [
        { value: 'total_sales_sum', label: 'Сумма продаж', format: 'currency' },
        { value: 'total_sales_profit', label: 'Прибыль', format: 'currency' },
        { value: 'total_sales_purchase', label: 'Себестоимость продаж', format: 'currency' },
        { value: 'total_sales_qty', label: 'Кол-во продаж', format: 'number' },
        { value: 'closing_qty', label: 'Остаток (шт)', format: 'number' },
        { value: 'closing_sum', label: 'Остаток (сумма)', format: 'currency' },
        { value: 'closing_purchase', label: 'Остаток (себест.)', format: 'currency' }
    ];

    const currentMetricOption = metricOptions.find(m => m.value === categoryMetric) || metricOptions[0];
    const formatMetricValue = (val) => currentMetricOption.format === 'currency' ? formatCurrency(val) : val.toFixed(0);

    const buildQueryString = useCallback(() => {
        const params = new URLSearchParams();
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);
        return params.toString();
    }, [startDate, endDate]);

    const fetchStores = useCallback(async () => {
        try {
            setLoading(true);
            const queryString = buildQueryString();
            const response = await loadList('/admin-api/stores');
            const storesData = await Promise.all(
                response.rows.map(async (store) => {
                    const jsonData = await storeService.getCategoryStats(store.id, queryString, token);
                    return { ...store, categoryStats: jsonData.response };
                })
            );
            setStores(storesData);
            if (storesData.length > 0 && selectedStores.length === 0) {
                setSelectedStores(storesData.slice(0, Math.min(3, storesData.length)).map(s => s.id));
            }
        } catch (error) {
            showToast('Ошибка загрузки магазинов', 'error');
        } finally {
            setLoading(false);
        }
    }, [buildQueryString, token, showToast, selectedStores.length]);

    useEffect(() => { fetchStores(); }, []);

    const toggleStore = (storeId) => {
        setSelectedStores(prev => 
            prev.includes(storeId) 
                ? prev.filter(id => id !== storeId)
                : [...prev, storeId]
        );
    };

    const toggleCategory = (categoryName) => {
        setHiddenCategories(prev => 
            prev.includes(categoryName) 
                ? prev.filter(c => c !== categoryName)
                : [...prev, categoryName]
        );
    };

    // Get all unique categories across selected stores
    const allCategories = new Map();
    stores
        .filter(s => selectedStores.includes(s.id))
        .forEach(store => {
            store.categoryStats?.data?.forEach(cat => {
                if (!allCategories.has(cat.product__categories__name)) {
                    allCategories.set(cat.product__categories__name, {
                        id: cat.product__categories__id,
                        name: cat.product__categories__name
                    });
                }
            });
        });

    // Helper function to export data to Excel
    const exportToExcel = (data, filename) => {
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Data');
        XLSX.writeFile(wb, `${filename}_${startDate}_${endDate}.xlsx`);
    };

    // Helper: Calculate category totals for sorting
    const categoryTotalsForSort = useMemo(() => {
        const totals = new Map();
        Array.from(allCategories.values()).forEach(category => {
            let totalSales = 0;
            let totalStock = 0;
            stores
                .filter(s => selectedStores.includes(s.id))
                .forEach(store => {
                    const catData = store.categoryStats?.data?.find(
                        c => c.product__categories__name === category.name
                    );
                    totalSales += catData?.total_sales_sum || 0;
                    totalStock += catData?.closing_sum || 0;
                });
            totals.set(category.name, { sales: totalSales, stock: totalStock });
        });
        return totals;
    }, [allCategories, stores, selectedStores]);

    // Sort categories based on sortBy selection
    const sortedCategories = useMemo(() => {
        return Array.from(allCategories.values())
            .filter(c => !hiddenCategories.includes(c.name))
            .sort((a, b) => {
            const aTotals = categoryTotalsForSort.get(a.name) || { sales: 0, stock: 0 };
            const bTotals = categoryTotalsForSort.get(b.name) || { sales: 0, stock: 0 };
            return sortBy === 'stock' 
                ? bTotals.stock - aTotals.stock 
                : bTotals.sales - aTotals.sales;
        });
    }, [allCategories, categoryTotalsForSort, sortBy, hiddenCategories]);

    // 1. UNIFIED CATEGORY METRIC COMPARISON - All categories with selected metric
    // First calculate grand total across ALL stores (including hidden) for percentage
    const categoryGrandTotals = useMemo(() => {
        const totals = new Map();
        stores.forEach(store => {
            const data = store.categoryStats?.data || [];
            data.forEach(c => {
                const catName = c.product__categories__name;
                if (!totals.has(catName)) {
                    totals.set(catName, 0);
                }
                totals.set(catName, totals.get(catName) + (c[categoryMetric] || 0));
            });
        });
        return totals;
    }, [stores, categoryMetric]);

    // Calculate overall grand total for the metric
    const metricGrandTotal = useMemo(() => {
        let total = 0;
        categoryGrandTotals.forEach(val => total += val);
        return total;
    }, [categoryGrandTotals]);

    const categoryMetricDataAll = useMemo(() => {
        return sortedCategories.map(category => {
            const row = { category: category.name };
            let selectedTotal = 0;
            stores
                .filter(s => selectedStores.includes(s.id))
                .forEach(store => {
                    const catData = store.categoryStats?.data?.find(
                        c => c.product__categories__name === category.name
                    );
                    const value = catData?.[categoryMetric] || 0;
                    row[store.name] = value;
                    selectedTotal += value;
                });
            row.total = selectedTotal;
            row.grandTotal = categoryGrandTotals.get(category.name) || 0;
            row.percentOfAll = metricGrandTotal > 0 ? (row.grandTotal / metricGrandTotal * 100) : 0;
            return row;
        });
    }, [sortedCategories, stores, selectedStores, categoryMetric, categoryGrandTotals, metricGrandTotal]);

    const categoryMetricData = categoryMetricDataAll.slice(0, topN);

    // 3. Selected store data - needed for multiple charts
    const selectedStoreData = stores.filter(s => selectedStores.includes(s.id));

    // 4. MARGIN VS TURNOVER SCATTER - Compare margin and turnover by category
    const marginTurnoverData = selectedStoreData.flatMap(store => {
        const data = store.categoryStats?.data || [];
        return data
            .filter(c => c.total_sales_sum > 0)
            .filter(c => !hiddenCategories.includes(c.product__categories__name))
            .map(c => {
                const margin = c.total_sales_sum > 0 
                    ? (c.total_sales_profit / c.total_sales_sum * 100) 
                    : 0;
                const turnover = (c.closing_purchase + c.total_sales_purchase) > 0 
                    ? (c.total_sales_purchase / (c.closing_purchase + c.total_sales_purchase) * 100) 
                    : 0;
                return {
                    store: store.name,
                    category: c.product__categories__name,
                    margin: margin,
                    turnover: turnover,
                    sales: c.total_sales_sum
                };
            });
    });

    

    // 5. STOCK VS SALES SCATTER - Compare inventory efficiency
    const scatterData = selectedStoreData.flatMap(store => {
        const data = store.categoryStats?.data || [];
        return data
            .filter(c => c.total_sales_sum > 0)
            .filter(c => !hiddenCategories.includes(c.product__categories__name))
            .slice(0, 20)
            .map(c => {
                const turnover = (c.closing_purchase + c.total_sales_purchase) > 0 
                    ? (c.total_sales_purchase / (c.closing_purchase + c.total_sales_purchase) * 100) 
                    : 0;
                return {
                    store: store.name,
                    category: c.product__categories__name,
                    x: c.closing_sum,
                    y: c.total_sales_sum,
                    z: c.total_sales_sum > 0 ? (c.total_sales_profit / c.total_sales_sum * 100) : 0,
                    turnover: turnover
                };
            });
    });

    // 6. STORE TOTALS SUMMARY
    const storeTotals = selectedStoreData.map(store => {
        const data = store.categoryStats?.data || [];
        const filteredData = data.filter(c => !hiddenCategories.includes(c.product__categories__name));
        return filteredData.reduce((acc, c) => ({
            store: store.name,
            sales: acc.sales + c.total_sales_sum,
            profit: acc.profit + c.total_sales_profit,
            stock: acc.stock + c.closing_sum,
            qty: acc.qty + c.total_sales_qty,
            categories: filteredData.length
        }), { store: store.name, sales: 0, profit: 0, stock: 0, qty: 0, categories: 0 });
    });

    // 7. STOCK VS SALES TABLE DATA - Categories as rows, stores as columns
    // Calculate totals across ALL stores (not just selected) for percentage calculation
    const stockVsSalesTableData = useMemo(() => {
        // First, calculate totals per category across ALL stores
        const categoryTotals = new Map();
        stores.forEach(store => {
            const data = store.categoryStats?.data || [];
            data.forEach(c => {
                const catName = c.product__categories__name;
                if (!categoryTotals.has(catName)) {
                    categoryTotals.set(catName, { totalSales: 0, totalStock: 0 });
                }
                const current = categoryTotals.get(catName);
                current.totalSales += c.total_sales_sum || 0;
                current.totalStock += c.closing_sum || 0;
            });
        });

        // Build table data with categories as rows
        return Array.from(allCategories.values())
            .filter(c => !hiddenCategories.includes(c.name))
            .map(category => {
                const catTotals = categoryTotals.get(category.name) || { totalSales: 0, totalStock: 0 };
                const storesData = {};
                
                stores
                    .filter(s => selectedStores.includes(s.id))
                    .forEach(store => {
                        const catData = store.categoryStats?.data?.find(
                            c => c.product__categories__name === category.name
                        );
                        if (catData) {
                            const sales = catData.total_sales_sum || 0;
                            const stock = catData.closing_sum || 0;
                            const turnover = (catData.closing_purchase + catData.total_sales_purchase) > 0 
                                ? (catData.total_sales_purchase / (catData.closing_purchase + catData.total_sales_purchase) * 100) 
                                : 0;
                            
                            storesData[store.name] = {
                                sales,
                                stock,
                                turnover,
                                salesPercent: catTotals.totalSales > 0 ? (sales / catTotals.totalSales * 100) : 0,
                                stockPercent: catTotals.totalStock > 0 ? (stock / catTotals.totalStock * 100) : 0
                            };
                        }
                    });

                return {
                    category: category.name,
                    stores: storesData,
                    totalSales: catTotals.totalSales,
                    totalStock: catTotals.totalStock
                };
            })
            .filter(row => row.totalSales > 0 || row.totalStock > 0)
            .sort((a, b) => sortBy === 'stock' ? b.totalStock - a.totalStock : b.totalSales - a.totalSales);
    }, [stores, selectedStores, allCategories, sortBy]);

    const views = [
        { id: 'category-metrics', label: '📊 По категориям' },
        { id: 'margins', label: '📈 Маржа vs Оборачиваемость' },
        { id: 'scatter', label: '📦 Склад vs Продажи' },
        { id: 'totals', label: '🏆 Итоги по магазинам' }
    ];

    const selectedStoresList = stores.filter(s => selectedStores.includes(s.id));
    
    // Fixed color mapping - maintain consistent colors for stores
    const storeColors = useMemo(() => {
        const colorMap = {};
        stores.forEach((store, i) => {
            colorMap[store.id] = COLORS[i % COLORS.length];
        });
        return colorMap;
    }, [stores]);

    const getStoreColor = (storeName) => {
        const store = stores.find(s => s.name === storeName);
        return store ? storeColors[store.id] : '#ccc';
    };

    return (
        <div className="store-comparison">
            {/* Filters */}
            <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                    <label>Начало: <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} /></label>
                    <label>Конец: <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} /></label>
                    <label>
                        Топ категорий: 
                        <select value={topN} onChange={e => setTopN(Number(e.target.value))} style={{ marginLeft: 8 }}>
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={15}>15</option>
                            <option value={20}>20</option>
                            <option value={30}>30</option>
                            <option value={40}>40</option>
                            <option value={50}>50</option>
                        </select>
                    </label>
                    <label>
                        Сортировать по: 
                        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ marginLeft: 8 }}>
                            <option value="sales">Продажи</option>
                            <option value="stock">Остатки</option>
                        </select>
                    </label>
                    <button onClick={fetchStores} disabled={loading}>
                        {loading ? 'Загрузка...' : 'Обновить'}
                    </button>
                </div>

                {/* Store selection */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 'bold', marginRight: 8 }}>Магазины:</span>
                    {stores.map((store, i) => (
                        <button
                            key={store.id}
                            onClick={() => toggleStore(store.id)}
                            style={{
                                padding: '6px 12px',
                                borderRadius: 4,
                                border: '2px solid',
                                borderColor: selectedStores.includes(store.id) ? storeColors[store.id] : '#ccc',
                                background: selectedStores.includes(store.id) ? storeColors[store.id] : '#fff',
                                color: selectedStores.includes(store.id) ? '#fff' : '#374151',
                                cursor: 'pointer',
                                fontWeight: selectedStores.includes(store.id) ? 'bold' : 'normal'
                            }}
                        >
                            {store.name}
                        </button>
                    ))}
                </div>

                {/* Category selection */}
                <div style={{ marginTop: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ fontWeight: 'bold' }}>Категории:</div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button 
                                onClick={() => setHiddenCategories([])}
                                style={{ fontSize: 12, padding: '4px 8px', cursor: 'pointer', background: '#f3f4f6', border: '1px solid #ddd', borderRadius: 4 }}
                            >
                                Показать все
                            </button>
                            <button 
                                onClick={() => setHiddenCategories(Array.from(allCategories.keys()))}
                                style={{ fontSize: 12, padding: '4px 8px', cursor: 'pointer', background: '#f3f4f6', border: '1px solid #ddd', borderRadius: 4 }}
                            >
                                Скрыть все
                            </button>
                        </div>
                    </div>
                    <div style={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 8, 
                        maxHeight: 150, 
                        overflowY: 'auto', 
                        border: '1px solid #eee', 
                        padding: 8, 
                        borderRadius: 4,
                        background: '#f9fafb'
                    }}>
                        {Array.from(allCategories.values()).sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(cat => (
                            <label key={cat.id} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer', userSelect: 'none', background: '#fff', padding: '2px 6px', borderRadius: 4, border: '1px solid #eee' }}>
                                <input 
                                    type="checkbox" 
                                    checked={!hiddenCategories.includes(cat.name)} 
                                    onChange={() => toggleCategory(cat.name)} 
                                />
                                {cat.name}
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            {/* KPI Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
                {storeTotals.map((store, i) => (
                    <div key={store.store} style={{ padding: 16, background: '#f8fafc', borderRadius: 8, borderLeft: `4px solid ${getStoreColor(store.store)}` }}>
                        <div style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 8 }}>{store.store}</div>
                        <div style={{ fontSize: 12, color: '#666' }}>Продажи: <strong>{formatCurrency(store.sales)}</strong></div>
                        <div style={{ fontSize: 12, color: '#666' }}>Прибыль: <strong style={{ color: '#16a34a' }}>{formatCurrency(store.profit)}</strong></div>
                        <div style={{ fontSize: 12, color: '#666' }}>Маржа: <strong>{store.sales > 0 ? (store.profit / store.sales * 100).toFixed(1) : 0}%</strong></div>
                        <div style={{ fontSize: 12, color: '#666' }}>Категорий: <strong>{store.categories}</strong></div>
                    </div>
                ))}
            </div>

            {/* View tabs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                {views.map(v => (
                    <button
                        key={v.id}
                        onClick={() => setActiveView(v.id)}
                        style={{
                            padding: '8px 16px',
                            borderRadius: 4,
                            border: 'none',
                            cursor: 'pointer',
                            background: activeView === v.id ? '#3b82f6' : '#e5e7eb',
                            color: activeView === v.id ? '#fff' : '#374151'
                        }}
                    >
                        {v.label}
                    </button>
                ))}
            </div>

            {/* Charts */}
            <div style={{ background: '#fff', padding: 24, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                {activeView === 'category-metrics' && (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                            <h3 style={{ margin: 0 }}>Топ {topN} категорий по</h3>
                            <select 
                                value={categoryMetric} 
                                onChange={e => setCategoryMetric(e.target.value)}
                                style={{ padding: '6px 12px', borderRadius: 4, border: '1px solid #ddd', fontSize: 14 }}
                            >
                                {metricOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                            <span style={{ color: '#666' }}>— сравнение магазинов</span>
                        </div>
                        <p style={{ color: '#666', marginBottom: 16 }}>Какие магазины лидируют в каждой категории. При наведении показывается % от общего по всем магазинам.</p>
                        <ResponsiveContainer width="100%" height={Math.max(400, topN * 50)}>
                            <BarChart data={categoryMetricData} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" tickFormatter={formatMetricValue} />
                                <YAxis dataKey="category" type="category" width={150} tick={{ fontSize: 11 }} />
                                <Tooltip 
                                    content={({ active, payload, label }) => {
                                        if (active && payload && payload.length) {
                                            const row = categoryMetricData.find(r => r.category === label);
                                            const grandTotal = row?.grandTotal || 0;
                                            const percentOfAll = row?.percentOfAll || 0;
                                            return (
                                                <div style={{ background: '#fff', border: '1px solid #ccc', padding: 10, borderRadius: 4, fontSize: 12 }}>
                                                    <div style={{ fontWeight: 'bold', marginBottom: 6 }}>{label}</div>
                                                    {payload.map((entry, idx) => {
                                                        const storePercent = grandTotal > 0 ? (entry.value / grandTotal * 100) : 0;
                                                        return (
                                                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 2 }}>
                                                                <span style={{ color: entry.color }}>{entry.name}:</span>
                                                                <span><strong>{formatMetricValue(entry.value)}</strong> ({storePercent.toFixed(1)}%)</span>
                                                            </div>
                                                        );
                                                    })}
                                                    <div style={{ borderTop: '1px solid #eee', marginTop: 6, paddingTop: 6 }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                                                            <span>Всего (все магазины):</span>
                                                            <strong>{formatMetricValue(grandTotal)}</strong>
                                                        </div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, color: '#666' }}>
                                                            <span>% от общего:</span>
                                                            <strong>{percentOfAll.toFixed(1)}%</strong>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }
                                        return null;
                                    }}
                                />
                                <Legend />
                                {selectedStoresList.map((store) => (
                                    <Bar key={store.id} dataKey={store.name} stackId="a" fill={storeColors[store.id]} />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                        
                        <div style={{ marginTop: 24 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                <h4>Данные таблицы (все категории) — {currentMetricOption.label}</h4>
                                <button 
                                    onClick={() => exportToExcel(categoryMetricDataAll.map(row => {
                                        const exportRow = { 'Категория': row.category };
                                        selectedStoresList.forEach(store => {
                                            exportRow[store.name] = row[store.name] || 0;
                                        });
                                        exportRow['Итого (выбранные)'] = row.total;
                                        exportRow['Всего (все магазины)'] = row.grandTotal;
                                        exportRow['% от общего'] = row.percentOfAll.toFixed(2);
                                        return exportRow;
                                    }), `category_${categoryMetric}_comparison`)}
                                    style={{ padding: '8px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                                >
                                    📥 Экспорт в Excel
                                </button>
                            </div>
                            <div style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                    <thead style={{ position: 'sticky', top: 0, background: '#f3f4f6' }}>
                                        <tr>
                                            <th style={{ border: '1px solid #ddd', padding: 8, textAlign: 'left' }}>Категория</th>
                                            {selectedStoresList.map(store => (
                                                <th key={store.id} style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right' }}>
                                                    {store.name}
                                                </th>
                                            ))}
                                            <th style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right', fontWeight: 'bold' }}>Итого</th>
                                            <th style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right', background: '#e0f2fe' }}>Все магазины</th>
                                            <th style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right', background: '#e0f2fe' }}>% от общего</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {categoryMetricDataAll.map((row, i) => (
                                            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                                                <td style={{ border: '1px solid #ddd', padding: 8 }}>{row.category}</td>
                                                {selectedStoresList.map(store => (
                                                    <td key={store.id} style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right' }}>
                                                        {formatMetricValue(row[store.name] || 0)}
                                                    </td>
                                                ))}
                                                <td style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right', fontWeight: 'bold' }}>
                                                    {formatMetricValue(row.total)}
                                                </td>
                                                <td style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right', background: '#f0f9ff' }}>
                                                    {formatMetricValue(row.grandTotal)}
                                                </td>
                                                <td style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right', background: '#f0f9ff' }}>
                                                    {row.percentOfAll.toFixed(1)}%
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                {activeView === 'margins' && (
                    <>
                        <h3>Маржа vs Оборачиваемость по категориям</h3>
                        <p style={{ color: '#666', marginBottom: 16 }}>X: Оборачиваемость % | Y: Маржа % | Размер: Продажи — Лучший квадрант: высокая маржа и высокая оборачиваемость</p>
                        <ResponsiveContainer width="100%" height={600}>
                            <ScatterChart margin={{ top: 20, right: 20, bottom: 60, left: 60 }}>
                                <CartesianGrid />
                                <XAxis 
                                    type="number"
                                    dataKey="turnover" 
                                    name="Оборачиваемость" 
                                    unit="%"
                                    label={{ value: 'Оборачиваемость %', position: 'bottom', offset: 40 }}
                                />
                                <YAxis 
                                    type="number"
                                    dataKey="margin" 
                                    name="Маржа" 
                                    unit="%"
                                    label={{ value: 'Маржа %', angle: -90, position: 'insideLeft', offset: 10 }}
                                />
                                <ZAxis dataKey="sales" range={[100, 500]} name="Продажи" />
                                <Tooltip 
                                    cursor={{ strokeDasharray: '3 3' }}
                                    content={({ active, payload }) => {
                                        if (active && payload && payload.length) {
                                            const data = payload[0].payload;
                                            return (
                                                <div style={{ background: '#fff', border: '1px solid #ccc', padding: 10, borderRadius: 4 }}>
                                                    <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{data.category}</div>
                                                    <div style={{ color: '#666', fontSize: 12 }}>Магазин: {data.store}</div>
                                                    <div style={{ fontSize: 12 }}>Маржа: {data.margin.toFixed(1)}%</div>
                                                    <div style={{ fontSize: 12 }}>Оборачиваемость: {data.turnover.toFixed(1)}%</div>
                                                    <div style={{ fontSize: 12 }}>Продажи: {formatCurrency(data.sales)}</div>
                                                </div>
                                            );
                                        }
                                        return null;
                                    }}
                                />
                                <Legend />
                                {selectedStoresList.map((store) => (
                                    <Scatter
                                        key={store.id}
                                        name={store.name}
                                        data={marginTurnoverData.filter(d => d.store === store.name)}
                                        fill={storeColors[store.id]}
                                    />
                                ))}
                            </ScatterChart>
                        </ResponsiveContainer>
                        
                        <div style={{ marginTop: 24 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                <h4>Данные таблицы</h4>
                                <button 
                                    onClick={() => exportToExcel(marginTurnoverData.map(d => ({
                                        'Магазин': d.store,
                                        'Категория': d.category,
                                        'Маржа %': d.margin,
                                        'Оборачиваемость %': d.turnover,
                                        'Продажи': d.sales
                                    })), 'margin_turnover_comparison')}
                                    style={{ padding: '8px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                                >
                                    📥 Экспорт в Excel
                                </button>
                            </div>
                            <div style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                    <thead style={{ position: 'sticky', top: 0, background: '#f3f4f6' }}>
                                        <tr>
                                            <th style={{ border: '1px solid #ddd', padding: 8, textAlign: 'left' }}>Магазин</th>
                                            <th style={{ border: '1px solid #ddd', padding: 8, textAlign: 'left' }}>Категория</th>
                                            <th style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right' }}>Маржа %</th>
                                            <th style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right' }}>Оборачиваемость %</th>
                                            <th style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right' }}>Продажи</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {marginTurnoverData.map((row, i) => (
                                            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                                                <td style={{ border: '1px solid #ddd', padding: 8 }}>{row.store}</td>
                                                <td style={{ border: '1px solid #ddd', padding: 8 }}>{row.category}</td>
                                                <td style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right' }}>
                                                    {row.margin.toFixed(1)}%
                                                </td>
                                                <td style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right' }}>
                                                    {row.turnover.toFixed(1)}%
                                                </td>
                                                <td style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right' }}>
                                                    {formatCurrency(row.sales)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}


                {activeView === 'scatter' && (
                    <>
                        <h3>Эффективность склада vs Продажи</h3>
                        <p style={{ color: '#666', marginBottom: 16 }}>X: Продажи | Y: Стоимость остатков | Размер: Маржа % — Лучший квадрант: высокие продажи при низких остатках</p>
                        <ResponsiveContainer width="100%" height={600}>
                            <ScatterChart margin={{ top: 20, right: 20, bottom: 60, left: 60 }}>
                                <CartesianGrid />
                                <XAxis 
                                    type="number"
                                    dataKey="y" 
                                    name="Продажи" 
                                    tickFormatter={formatCurrency}
                                    label={{ value: 'Продажи', position: 'bottom', offset: 40 }}
                                />
                                <YAxis 
                                    type="number"
                                    dataKey="x" 
                                    name="Остатки" 
                                    tickFormatter={formatCurrency}
                                    label={{ value: 'Стоимость остатков', angle: -90, position: 'insideLeft', offset: 10 }}
                                />
                                <ZAxis dataKey="z" range={[100, 200]} name="Маржа %" />
                                <Tooltip 
                                    cursor={{ strokeDasharray: '3 3' }}
                                    content={({ active, payload }) => {
                                        if (active && payload && payload.length) {
                                            const data = payload[0].payload;
                                            return (
                                                <div style={{ background: '#fff', border: '1px solid #ccc', padding: 10, borderRadius: 4 }}>
                                                    <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{data.category}</div>
                                                    <div style={{ color: '#666', fontSize: 12 }}>Магазин: {data.store}</div>
                                                    <div style={{ fontSize: 12 }}>Продажи: {formatCurrency(data.y)}</div>
                                                    <div style={{ fontSize: 12 }}>Остатки: {formatCurrency(data.x)}</div>
                                                    <div style={{ fontSize: 12 }}>Маржа: {data.z.toFixed(1)}%</div>
                                                    <div style={{ fontSize: 12 }}>Оборачиваемость: {data.turnover.toFixed(1)}%</div>
                                                </div>
                                            );
                                        }
                                        return null;
                                    }}
                                />
                                <Legend />
                                {selectedStoresList.map((store) => (
                                    <Scatter
                                        key={store.id}
                                        name={store.name}
                                        data={scatterData.filter(d => d.store === store.name)}
                                        fill={storeColors[store.id]}
                                    />
                                ))}
                            </ScatterChart>
                        </ResponsiveContainer>
                        
                        <div style={{ marginTop: 24 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                <h4>Данные таблицы — Сравнение магазинов по категориям</h4>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button 
                                        onClick={() => {
                                            // Export as HTML with full styling
                                            const numStores = selectedStoresList.length;
                                            
                                            // Helper to get percentage-based color
                                            const getPercentBgColor = (percent, type) => {
                                                const intensity = Math.min(percent / 100, 1);
                                                if (type === 'sales') {
                                                    return `rgba(34, 197, 94, ${intensity * 0.5})`;
                                                } else if (type === 'stock') {
                                                    return `rgba(234, 179, 8, ${intensity * 0.5})`;
                                                } else { // turnover
                                                    return `rgba(59, 130, 246, ${intensity * 0.4})`;
                                                }
                                            };
                                            
                                            let html = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Склад vs Продажи - ${startDate} - ${endDate}</title>
    <style>
        body { font-family: Arial, sans-serif; font-size: 11px; }
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid #ddd; padding: 6px 8px; }
        th { font-weight: bold; }
        .header-category { background: #e5e7eb; }
        .header-turnover { background: #dbeafe; text-align: center; }
        .header-store { color: #fff; text-align: center; }
        .header-sales { background: #dcfce7; text-align: center; font-size: 10px; }
        .header-stock { background: #fef3c7; text-align: center; font-size: 10px; }
        .header-total { background: #e5e7eb; text-align: center; }
        .cell-category { font-weight: 500; }
        .cell-turnover { text-align: center; }
        .cell-sales { text-align: right; }
        .cell-stock { text-align: right; }
        .cell-total-sales { text-align: right; font-weight: bold; background: #f0fdf4; }
        .cell-total-stock { text-align: right; font-weight: bold; background: #fefce8; }
        .percent { font-size: 10px; color: #666; }
        .legend { margin-top: 12px; font-size: 11px; color: #666; }
    </style>
</head>
<body>
    <h2>Эффективность склада vs Продажи</h2>
    <p>Период: ${startDate} — ${endDate}</p>
    <table>
        <thead>
            <tr>
                <th rowspan="2" class="header-category">Категория</th>
                <th colspan="${numStores}" class="header-turnover">Оборачиваемость %</th>
                ${selectedStoresList.map(store => `<th colspan="2" class="header-store" style="background: ${storeColors[store.id]}">${store.name}</th>`).join('')}
                <th colspan="2" class="header-total">Всего</th>
            </tr>
            <tr>
                ${selectedStoresList.map(store => `<th class="header-turnover" style="font-size: 10px;">${store.name.substring(0, 8)}</th>`).join('')}
                ${selectedStoresList.map(() => `<th class="header-sales">Продажи</th><th class="header-stock">Остатки</th>`).join('')}
                <th class="header-sales">Продажи</th>
                <th class="header-stock">Остатки</th>
            </tr>
        </thead>
        <tbody>
`;
                                            
                                            stockVsSalesTableData.forEach((row, i) => {
                                                const rowBg = i % 2 === 0 ? '#fff' : '#f9fafb';
                                                html += `<tr style="background: ${rowBg}">`;
                                                html += `<td class="cell-category">${row.category}</td>`;
                                                
                                                // Turnover columns
                                                selectedStoresList.forEach(store => {
                                                    const storeData = row.stores[store.name] || {};
                                                    const turnover = storeData.turnover || 0;
                                                    const bg = getPercentBgColor(turnover, 'turnover');
                                                    html += `<td class="cell-turnover" style="background: ${bg}">${turnover.toFixed(1)}%</td>`;
                                                });
                                                
                                                // Sales and Stock columns
                                                selectedStoresList.forEach(store => {
                                                    const storeData = row.stores[store.name] || {};
                                                    const salesPercent = storeData.salesPercent || 0;
                                                    const stockPercent = storeData.stockPercent || 0;
                                                    const salesBg = getPercentBgColor(salesPercent, 'sales');
                                                    const stockBg = getPercentBgColor(stockPercent, 'stock');
                                                    html += `<td class="cell-sales" style="background: ${salesBg}">${formatCurrency(storeData.sales || 0)}<br><span class="percent">${salesPercent.toFixed(1)}%</span></td>`;
                                                    html += `<td class="cell-stock" style="background: ${stockBg}">${formatCurrency(storeData.stock || 0)}<br><span class="percent">${stockPercent.toFixed(1)}%</span></td>`;
                                                });
                                                
                                                // Totals
                                                html += `<td class="cell-total-sales">${formatCurrency(row.totalSales)}</td>`;
                                                html += `<td class="cell-total-stock">${formatCurrency(row.totalStock)}</td>`;
                                                html += `</tr>`;
                                            });
                                            
                                            html += `
        </tbody>
    </table>
    <div class="legend">
        <strong>Легенда:</strong> Проценты рассчитаны относительно всех магазинов (не только выбранных). 
        Интенсивность цвета показывает долю от 0% до 100%.
    </div>
</body>
</html>`;
                                            
                                            // Download as HTML file
                                            const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
                                            const url = URL.createObjectURL(blob);
                                            const a = document.createElement('a');
                                            a.href = url;
                                            a.download = `stock_vs_sales_${startDate}_${endDate}.html`;
                                            a.click();
                                            URL.revokeObjectURL(url);
                                        }}
                                        style={{ padding: '8px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                                    >
                                        🎨 HTML (с цветами)
                                    </button>
                                    <button 
                                        onClick={() => {
                                            // Build Excel with two-level headers
                                            const wb = XLSX.utils.book_new();
                                            const numStores = selectedStoresList.length;
                                            const data = [];
                                            
                                            // Row 1: First level headers
                                            const header1 = ['Категория'];
                                            header1.push('Оборачиваемость %');
                                            for (let i = 1; i < numStores; i++) header1.push('');
                                            selectedStoresList.forEach(store => {
                                                header1.push(store.name);
                                                header1.push('');
                                            });
                                            header1.push('Всего');
                                            header1.push('');
                                            data.push(header1);
                                            
                                            // Row 2: Second level headers
                                            const header2 = [''];
                                            selectedStoresList.forEach(store => header2.push(store.name));
                                            selectedStoresList.forEach(() => {
                                                header2.push('Продажи');
                                                header2.push('Остатки');
                                            });
                                            header2.push('Продажи');
                                            header2.push('Остатки');
                                            data.push(header2);
                                            
                                            // Data rows with separate columns for values and percentages
                                            stockVsSalesTableData.forEach(row => {
                                                const dataRow = [row.category];
                                                selectedStoresList.forEach(store => {
                                                    const storeData = row.stores[store.name] || {};
                                                    dataRow.push((storeData.turnover || 0).toFixed(1) + '%');
                                                });
                                                selectedStoresList.forEach(store => {
                                                    const storeData = row.stores[store.name] || {};
                                                    dataRow.push(`${formatCurrency(storeData.sales || 0)} (${(storeData.salesPercent || 0).toFixed(1)}%)`);
                                                    dataRow.push(`${formatCurrency(storeData.stock || 0)} (${(storeData.stockPercent || 0).toFixed(1)}%)`);
                                                });
                                                dataRow.push(formatCurrency(row.totalSales));
                                                dataRow.push(formatCurrency(row.totalStock));
                                                data.push(dataRow);
                                            });
                                            
                                            const ws = XLSX.utils.aoa_to_sheet(data);
                                            
                                            // Set column widths
                                            const colWidths = [{ wch: 25 }];
                                            for (let i = 0; i < numStores; i++) colWidths.push({ wch: 12 });
                                            for (let i = 0; i < numStores * 2; i++) colWidths.push({ wch: 18 });
                                            colWidths.push({ wch: 12 }, { wch: 12 });
                                            ws['!cols'] = colWidths;
                                            
                                            // Define merges
                                            const merges = [
                                                { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } },
                                                { s: { r: 0, c: 1 }, e: { r: 0, c: numStores } },
                                            ];
                                            let colOffset = numStores + 1;
                                            selectedStoresList.forEach((store, i) => {
                                                merges.push({ s: { r: 0, c: colOffset + i * 2 }, e: { r: 0, c: colOffset + i * 2 + 1 } });
                                            });
                                            const totalColStart = colOffset + numStores * 2;
                                            merges.push({ s: { r: 0, c: totalColStart }, e: { r: 0, c: totalColStart + 1 } });
                                            ws['!merges'] = merges;
                                            
                                            XLSX.utils.book_append_sheet(wb, ws, 'Склад vs Продажи');
                                            XLSX.writeFile(wb, `stock_vs_sales_${startDate}_${endDate}.xlsx`);
                                        }}
                                        style={{ padding: '8px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                                    >
                                        📥 Excel
                                    </button>
                                </div>
                            </div>
                            <div style={{ overflowX: 'auto', maxHeight: '500px', overflowY: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                    <thead style={{ position: 'sticky', top: 0, background: '#f3f4f6', zIndex: 10 }}>
                                        {/* First header row - KPI groups */}
                                        <tr>
                                            <th rowSpan={2} style={{ border: '1px solid #ddd', padding: 8, textAlign: 'left', minWidth: 150, background: '#e5e7eb' }}>
                                                Категория
                                            </th>
                                            <th colSpan={selectedStoresList.length} style={{ border: '1px solid #ddd', padding: 8, textAlign: 'center', background: '#dbeafe' }}>
                                                Оборачиваемость %
                                            </th>
                                            {selectedStoresList.map(store => (
                                                <th key={store.id} colSpan={2} style={{ border: '1px solid #ddd', padding: 8, textAlign: 'center', background: storeColors[store.id], color: '#fff' }}>
                                                    {store.name}
                                                </th>
                                            ))}
                                            <th colSpan={2} rowSpan={2} style={{ border: '1px solid #ddd', padding: 8, textAlign: 'center', background: '#e5e7eb' }}>
                                                Всего
                                            </th>
                                        </tr>
                                        {/* Second header row - Store names for turnover, Sales/Stock for each store */}
                                        <tr>
                                            {selectedStoresList.map(store => (
                                                <th key={`turn-${store.id}`} style={{ border: '1px solid #ddd', padding: 6, textAlign: 'center', background: '#dbeafe', fontSize: 10, minWidth: 60 }}>
                                                    {store.name.substring(0, 8)}
                                                </th>
                                            ))}
                                            {selectedStoresList.map(store => (
                                                <React.Fragment key={`sales-stock-${store.id}`}>
                                                    <th style={{ border: '1px solid #ddd', padding: 6, textAlign: 'center', background: '#dcfce7', fontSize: 10, minWidth: 80 }}>
                                                        Продажи
                                                    </th>
                                                    <th style={{ border: '1px solid #ddd', padding: 6, textAlign: 'center', background: '#fef3c7', fontSize: 10, minWidth: 80 }}>
                                                        Остатки
                                                    </th>
                                                </React.Fragment>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {stockVsSalesTableData.map((row, i) => (
                                            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                                                <td style={{ border: '1px solid #ddd', padding: 8, fontWeight: 500 }}>{row.category}</td>
                                                {/* Turnover columns for each store */}
                                                {selectedStoresList.map(store => {
                                                    const storeData = row.stores[store.name] || {};
                                                    const turnover = storeData.turnover || 0;
                                                    const bgColor = `rgba(59, 130, 246, ${Math.min(turnover / 100, 1) * 0.4})`;
                                                    return (
                                                        <td key={`turn-${store.id}`} style={{ border: '1px solid #ddd', padding: 6, textAlign: 'center', background: bgColor }}>
                                                            {turnover.toFixed(1)}%
                                                        </td>
                                                    );
                                                })}
                                                {/* Sales and Stock columns for each store (adjacent) */}
                                                {selectedStoresList.map(store => {
                                                    const storeData = row.stores[store.name] || {};
                                                    const salesPercent = storeData.salesPercent || 0;
                                                    const stockPercent = storeData.stockPercent || 0;
                                                    const salesBgColor = `rgba(34, 197, 94, ${Math.min(salesPercent / 100, 1) * 0.5})`;
                                                    const stockBgColor = `rgba(234, 179, 8, ${Math.min(stockPercent / 100, 1) * 0.5})`;
                                                    return (
                                                        <React.Fragment key={`data-${store.id}`}>
                                                            <td style={{ border: '1px solid #ddd', padding: 6, textAlign: 'right', background: salesBgColor }}>
                                                                <div>{formatCurrency(storeData.sales || 0)}</div>
                                                                <div style={{ fontSize: 10, color: '#666' }}>{salesPercent.toFixed(1)}%</div>
                                                            </td>
                                                            <td style={{ border: '1px solid #ddd', padding: 6, textAlign: 'right', background: stockBgColor }}>
                                                                <div>{formatCurrency(storeData.stock || 0)}</div>
                                                                <div style={{ fontSize: 10, color: '#666' }}>{stockPercent.toFixed(1)}%</div>
                                                            </td>
                                                        </React.Fragment>
                                                    );
                                                })}
                                                {/* Total columns */}
                                                <td style={{ border: '1px solid #ddd', padding: 6, textAlign: 'right', fontWeight: 'bold', background: '#f0fdf4' }}>
                                                    {formatCurrency(row.totalSales)}
                                                </td>
                                                <td style={{ border: '1px solid #ddd', padding: 6, textAlign: 'right', fontWeight: 'bold', background: '#fefce8' }}>
                                                    {formatCurrency(row.totalStock)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div style={{ marginTop: 12, fontSize: 11, color: '#666' }}>
                                <strong>Легенда:</strong> Проценты рассчитаны относительно всех магазинов (не только выбранных). 
                                Интенсивность цвета показывает долю от 0% до 100%.
                            </div>
                        </div>
                    </>
                )}

                {activeView === 'totals' && (
                    <>
                        <h3>Итоговые показатели по магазинам</h3>
                        <p style={{ color: '#666', marginBottom: 16 }}>Прямое сравнение общих метрик</p>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart data={storeTotals}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="store" />
                                <YAxis tickFormatter={formatCurrency} />
                                <Tooltip formatter={(val) => formatCurrency(val)} />
                                <Legend />
                                <Bar dataKey="sales" fill="#8884d8" name="Продажи" />
                                <Bar dataKey="profit" fill="#82ca9d" name="Прибыль" />
                                <Bar dataKey="stock" fill="#ffc658" name="Остатки" />
                            </BarChart>
                        </ResponsiveContainer>
                        
                        <div style={{ marginTop: 24 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                <h4>Данные таблицы</h4>
                                <button 
                                    onClick={() => exportToExcel(storeTotals.map(s => ({
                                        'Магазин': s.store,
                                        'Продажи': s.sales,
                                        'Прибыль': s.profit,
                                        'Остатки': s.stock,
                                        'Количество продаж': s.qty,
                                        'Категорий': s.categories,
                                        'Маржа %': s.sales > 0 ? (s.profit / s.sales * 100).toFixed(2) : 0
                                    })), 'store_totals')}
                                    style={{ padding: '8px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                                >
                                    📥 Экспорт в Excel
                                </button>
                            </div>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                    <thead style={{ background: '#f3f4f6' }}>
                                        <tr>
                                            <th style={{ border: '1px solid #ddd', padding: 8, textAlign: 'left' }}>Магазин</th>
                                            <th style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right' }}>Продажи</th>
                                            <th style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right' }}>Прибыль</th>
                                            <th style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right' }}>Маржа %</th>
                                            <th style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right' }}>Остатки</th>
                                            <th style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right' }}>Кол-во продаж</th>
                                            <th style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right' }}>Категорий</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {storeTotals.map((row, i) => (
                                            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                                                <td style={{ border: '1px solid #ddd', padding: 8, fontWeight: 'bold' }}>{row.store}</td>
                                                <td style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right' }}>
                                                    {formatCurrency(row.sales)}
                                                </td>
                                                <td style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right', color: '#16a34a' }}>
                                                    {formatCurrency(row.profit)}
                                                </td>
                                                <td style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right' }}>
                                                    {row.sales > 0 ? (row.profit / row.sales * 100).toFixed(1) : 0}%
                                                </td>
                                                <td style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right' }}>
                                                    {formatCurrency(row.stock)}
                                                </td>
                                                <td style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right' }}>
                                                    {row.qty.toFixed(0)}
                                                </td>
                                                <td style={{ border: '1px solid #ddd', padding: 8, textAlign: 'right' }}>
                                                    {row.categories}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Info */}
            <div style={{ marginTop: 16, color: '#666', fontSize: 14 }}>
                Выбрано магазинов: {selectedStores.length} | Всего категорий: {allCategories.size} | Период: {startDate} — {endDate}
            </div>
        </div>
    );
};

export default StoreCategoryComparison;
